/**
 * Spatial voice: a WebRTC mesh (one peer per room member) with each remote stream routed
 * through a PannerNode at that member's head position. Inverse-distance rolloff, reference
 * 1 m, max 15 m, and a low-pass that closes beyond 8 m. Local voice activity comes from an
 * AnalyserNode on the microphone and is sent in the pose frame.
 *
 * A screen share rides the same peer connections as a video track (plus its audio, played
 * flat, not spatialised); receivers get it through `onVideo`.
 */
import * as THREE from 'three';
import { Peer } from './PeerConnection.ts';

interface RemoteVoice {
  peer: Peer;
  stream?: MediaStream;
  audio?: HTMLAudioElement;
  source?: MediaStreamAudioSourceNode;
  panner?: PannerNode;
  filter?: BiquadFilterNode;
  gain?: GainNode;
  /** Measures the voice's loudness (drives the avatar's mouth). */
  analyser?: AnalyserNode;
  state: RTCPeerConnectionState;
}

export interface VoiceMeshOptions {
  sendSignal: (to: string, data: unknown) => void;
  onPeerState?: (id: string, state: RTCPeerConnectionState) => void;
  /** A remote member's screen share started (stream) or ended (undefined). */
  onVideo?: (id: string, stream: MediaStream | undefined) => void;
}

const _pos = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _up = new THREE.Vector3();

export class VoiceMesh {
  private ctx: AudioContext | undefined;
  private localStream: MediaStream | undefined;
  private analyser: AnalyserNode | undefined;
  private analyserData: Uint8Array<ArrayBuffer> | undefined;
  private readonly remotes = new Map<string, RemoteVoice>();
  private muted = false;
  private nonSpatial = new Set<string>();
  /** The local screen share, sent to every peer. */
  private shareStream: MediaStream | undefined;
  private readonly shareAudio = new Map<string, HTMLAudioElement>();
  /** True while the local microphone registers speech. */
  speaking = false;
  /** Bumped whenever the microphone is opened again (its listeners re-attach). */
  micGeneration = 0;
  private micSource: MediaStreamAudioSourceNode | undefined;
  private reopening = false;
  private lastMicCheck = 0;
  micError: string | undefined;

  constructor(private readonly options: VoiceMeshOptions) {}

  get ready(): boolean {
    return !!this.localStream;
  }

  get peerCount(): number {
    return this.remotes.size;
  }

  peerStates(): Record<string, RTCPeerConnectionState> {
    const out: Record<string, RTCPeerConnectionState> = {};
    for (const [id, r] of this.remotes) out[id] = r.state;
    return out;
  }

  /** Requests the microphone. Call from a user gesture. Safe to call again. */
  async start(ctx: AudioContext): Promise<void> {
    this.ctx = ctx;
    if (this.localStream && this.micLive) return;
    await this.openMic();
  }

  /** Whether the microphone track is still capturing. */
  get micLive(): boolean {
    return this.localStream?.getAudioTracks()[0]?.readyState === 'live';
  }

  /**
   * Opens the microphone (again): the one capture the app holds. Voice chat, the meeting
   * transcript and questions to Luma all use it; on the Quest a second capture ended the
   * first (2026-10-06: the voice-chat track was found ended, so nobody heard the speaker).
   */
  private async openMic(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });
      const old = this.localStream;
      this.localStream = stream;
      for (const track of old?.getTracks() ?? []) track.stop();
      this.micError = undefined;
      if (!this.analyser) {
        this.analyser = ctx.createAnalyser();
        this.analyser.fftSize = 512;
        this.analyserData = new Uint8Array(this.analyser.fftSize);
      }
      this.micSource?.disconnect();
      this.micSource = ctx.createMediaStreamSource(stream);
      this.micSource.connect(this.analyser);
      this.applyMute();
      const track = stream.getAudioTracks()[0] ?? null;
      track?.addEventListener('ended', () => void this.reopenMic());
      // Peers created before the mic was ready (or holding the old track) get this one.
      for (const r of this.remotes.values()) r.peer.replaceTrack(track);
      this.micGeneration++;
    } catch (err) {
      this.micError = (err as Error).message;
      console.warn('[voice] microphone unavailable:', this.micError);
    }
  }

  /** The microphone stopped (another capture, the headset slept): open it again. */
  private async reopenMic(): Promise<void> {
    if (this.reopening || !this.ctx) return;
    this.reopening = true;
    console.warn('[voice] the microphone stopped; opening it again');
    try {
      await new Promise((r) => setTimeout(r, 500));
      if (!this.micLive) await this.openMic();
    } finally {
      this.reopening = false;
    }
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyMute();
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /** The local microphone and the audio context it runs in (the transcriber listens too). */
  get microphone(): { stream: MediaStream; context: AudioContext } | undefined {
    return this.localStream && this.ctx
      ? { stream: this.localStream, context: this.ctx }
      : undefined;
  }

  private applyMute(): void {
    for (const track of this.localStream?.getAudioTracks() ?? []) track.enabled = !this.muted;
  }

  /**
   * Starts or stops sending a screen share to every peer (current and future). Tracks are
   * added to the existing connections, which renegotiate on their own.
   */
  setShareStream(stream: MediaStream | undefined): void {
    const previous = this.shareStream;
    if (previous)
      for (const r of this.remotes.values())
        for (const track of previous.getTracks()) r.peer.removeTrack(track);
    this.shareStream = stream;
    if (stream)
      for (const r of this.remotes.values())
        for (const track of stream.getTracks()) r.peer.addTrack(track, stream);
  }

  get sharing(): boolean {
    return !!this.shareStream;
  }

  /** Presenter mode: a member's voice plays at full volume without spatialisation. */
  setNonSpatial(id: string, on: boolean): void {
    if (on) this.nonSpatial.add(id);
    else this.nonSpatial.delete(id);
  }

  /* ---------- Peers ---------- */

  addPeer(id: string, initiator: boolean): void {
    if (this.remotes.has(id)) return;
    const remote: RemoteVoice = { state: 'new', peer: undefined as unknown as Peer };
    remote.peer = new Peer({
      initiator,
      stream: this.localStream,
      onSignal: (data) => this.options.sendSignal(id, data),
      onStream: (stream) => this.attachStream(id, stream),
      onState: (state) => {
        remote.state = state;
        this.options.onPeerState?.(id, state);
        if (state === 'failed' || state === 'closed') this.removePeer(id);
      },
    });
    this.remotes.set(id, remote);
    if (this.shareStream)
      for (const track of this.shareStream.getTracks())
        remote.peer.addTrack(track, this.shareStream);
  }

  removePeer(id: string): void {
    const r = this.remotes.get(id);
    if (!r) return;
    this.remotes.delete(id);
    r.peer.close();
    r.source?.disconnect();
    r.panner?.disconnect();
    r.filter?.disconnect();
    r.gain?.disconnect();
    if (r.audio) {
      r.audio.srcObject = null;
      r.audio.remove();
    }
    this.dropShare(id);
  }

  private dropShare(id: string): void {
    const audio = this.shareAudio.get(id);
    if (audio) {
      audio.srcObject = null;
      audio.remove();
      this.shareAudio.delete(id);
      this.options.onVideo?.(id, undefined);
    }
  }

  removeAll(): void {
    for (const id of [...this.remotes.keys()]) this.removePeer(id);
  }

  signal(from: string, data: unknown): void {
    const r = this.remotes.get(from);
    if (!r) {
      // Offer from a member we have not met yet (they joined before us): answer it.
      this.addPeer(from, false);
      void this.remotes.get(from)!.peer.signal(data);
      return;
    }
    void r.peer.signal(data);
  }

  private attachStream(id: string, stream: MediaStream): void {
    const r = this.remotes.get(id);
    if (!r || !this.ctx) return;
    if (stream.getVideoTracks().length > 0) {
      this.attachShare(id, stream);
      return;
    }
    if (r.stream === stream) return;
    r.stream = stream;
    // Chrome only delivers WebRTC audio into WebAudio once the stream is bound to a media element.
    const audio = document.createElement('audio');
    audio.srcObject = stream;
    audio.muted = true;
    audio.autoplay = true;
    audio.setAttribute('playsinline', '');
    audio.style.display = 'none';
    document.body.append(audio);
    void audio.play().catch(() => {});
    r.audio = audio;

    const ctx = this.ctx;
    r.source = ctx.createMediaStreamSource(stream);
    r.panner = ctx.createPanner();
    r.panner.panningModel = 'HRTF';
    r.panner.distanceModel = 'inverse';
    r.panner.refDistance = 1;
    r.panner.maxDistance = 15;
    r.panner.rolloffFactor = 1;
    r.filter = ctx.createBiquadFilter();
    r.filter.type = 'lowpass';
    r.filter.frequency.value = 20000;
    r.gain = ctx.createGain();
    r.gain.gain.value = 1;
    r.source.connect(r.filter).connect(r.panner).connect(r.gain).connect(ctx.destination);
    r.analyser = ctx.createAnalyser();
    r.analyser.fftSize = 256;
    r.source.connect(r.analyser);
  }

  private readonly levelData = new Uint8Array(256);

  /** How loud a remote person is speaking now, 0..1 (undefined without their audio). */
  level(id: string): number | undefined {
    const a = this.remotes.get(id)?.analyser;
    if (!a) return undefined;
    a.getByteTimeDomainData(this.levelData);
    let sum = 0;
    for (const v of this.levelData) sum += ((v - 128) / 128) ** 2;
    return Math.min(1, Math.sqrt(sum / this.levelData.length) * 5);
  }

  /** A remote screen share: its audio plays flat through a plain element, video goes to the wall. */
  private attachShare(id: string, stream: MediaStream): void {
    if (this.shareAudio.has(id)) return;
    const audio = document.createElement('audio');
    audio.srcObject = stream;
    audio.autoplay = true;
    audio.setAttribute('playsinline', '');
    audio.style.display = 'none';
    document.body.append(audio);
    void audio.play().catch(() => {});
    this.shareAudio.set(id, audio);
    const video = stream.getVideoTracks()[0];
    const ended = () => {
      if (this.shareAudio.get(id) === audio) this.dropShare(id);
    };
    video?.addEventListener('ended', ended);
    video?.addEventListener('mute', ended);
    stream.addEventListener('removetrack', (e) => {
      if (e.track.kind === 'video') ended();
    });
    this.options.onVideo?.(id, stream);
  }

  /* ---------- Per frame ---------- */

  /** Updates listener and panner positions. `heads` maps session id to world head position/orientation. */
  update(
    listener: THREE.Camera,
    heads: Map<string, { position: THREE.Vector3; quaternion: THREE.Quaternion }>,
  ): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = ctx.currentTime;
    listener.getWorldPosition(_pos);
    _fwd.set(0, 0, -1).applyQuaternion(listener.getWorldQuaternion(new THREE.Quaternion()));
    _up.set(0, 1, 0).applyQuaternion(listener.getWorldQuaternion(new THREE.Quaternion()));
    const l = ctx.listener;
    if (l.positionX) {
      l.positionX.setTargetAtTime(_pos.x, t, 0.02);
      l.positionY.setTargetAtTime(_pos.y, t, 0.02);
      l.positionZ.setTargetAtTime(_pos.z, t, 0.02);
      l.forwardX.setTargetAtTime(_fwd.x, t, 0.02);
      l.forwardY.setTargetAtTime(_fwd.y, t, 0.02);
      l.forwardZ.setTargetAtTime(_fwd.z, t, 0.02);
      l.upX.setTargetAtTime(_up.x, t, 0.02);
      l.upY.setTargetAtTime(_up.y, t, 0.02);
      l.upZ.setTargetAtTime(_up.z, t, 0.02);
    } else {
      (l as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(
        _pos.x,
        _pos.y,
        _pos.z,
      );
    }
    for (const [id, r] of this.remotes) {
      if (!r.panner || !r.filter || !r.gain) continue;
      const head = heads.get(id);
      if (this.nonSpatial.has(id)) {
        r.panner.positionX.setTargetAtTime(_pos.x, t, 0.05);
        r.panner.positionY.setTargetAtTime(_pos.y, t, 0.05);
        r.panner.positionZ.setTargetAtTime(_pos.z, t, 0.05);
        r.filter.frequency.setTargetAtTime(20000, t, 0.1);
        r.gain.gain.setTargetAtTime(1, t, 0.1);
        continue;
      }
      if (!head) continue;
      r.panner.positionX.setTargetAtTime(head.position.x, t, 0.05);
      r.panner.positionY.setTargetAtTime(head.position.y, t, 0.05);
      r.panner.positionZ.setTargetAtTime(head.position.z, t, 0.05);
      const d = head.position.distanceTo(_pos);
      // Mild low-pass beyond 8 m: 20 kHz down to about 2 kHz at 15 m.
      const cutoff =
        d <= 8
          ? 20000
          : THREE.MathUtils.lerp(20000, 2000, THREE.MathUtils.clamp((d - 8) / 7, 0, 1));
      r.filter.frequency.setTargetAtTime(cutoff, t, 0.1);
      r.gain.gain.setTargetAtTime(1, t, 0.1);
    }
    this.updateVad();
    // Not every browser reports the end of a capture: look every two seconds.
    const now = performance.now();
    if (now - this.lastMicCheck > 2000) {
      this.lastMicCheck = now;
      if (this.localStream && !this.micLive) void this.reopenMic();
    }
  }

  private updateVad(): void {
    if (!this.analyser || !this.analyserData || this.muted) {
      this.speaking = false;
      return;
    }
    this.analyser.getByteTimeDomainData(this.analyserData);
    let sum = 0;
    for (let i = 0; i < this.analyserData.length; i++) {
      const v = (this.analyserData[i]! - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / this.analyserData.length);
    // Hysteresis so the ring does not flicker.
    this.speaking = this.speaking ? rms > 0.02 : rms > 0.035;
  }

  dispose(): void {
    this.setShareStream(undefined);
    this.removeAll();
    for (const track of this.localStream?.getAudioTracks() ?? []) track.stop();
    this.localStream = undefined;
  }
}
